/**
 * The ONE replay-consent predicate (BOO-5163, #9221), shared by every surface that records a visitor's
 * session with the main PostHog instance: apps/frontend and apps/landing. (apps/accounting ships no
 * posthog-js; the auth-only and pre-consent instances of #9144 / #9200 are consent-independent and
 * governed by their own flag.)
 *
 * With the toggle ON, "accepted" on the cookie banner grants usage measurement only; recording also
 * needs the separate replay choice, given in the sign-up form and the privacy settings. With the toggle
 * OFF, an analytics accept starts the recording, as before.
 *
 * The replay choice itself is stored per app origin (app.bookeper.pl, localStorage), so a surface that
 * has no such storage passes `replayGranted: false` and never records while the toggle is on.
 */
export function isReplayConsentToggleOn(raw: string | undefined): boolean {
  const normalized = raw?.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'on';
}

export interface MainSessionRecordingInput {
  /** The analytics decision is "accepted". */
  analyticsAccepted: boolean;
  /** `isReplayConsentToggleOn` of the surface's flag. */
  toggleOn: boolean;
  /** The visitor's explicit replay choice, as this surface can read it. */
  replayGranted: boolean;
}

/** May the main instance record this visitor? Fails closed: anything but an explicit yes is no. */
export function mayRecordMainSession({
  analyticsAccepted,
  toggleOn,
  replayGranted,
}: MainSessionRecordingInput): boolean {
  if (!analyticsAccepted) return false;
  return toggleOn ? replayGranted : true;
}
